// src/components/InsightsPanel.js
// Rotating "Did you know?" insights computed from player data.
// Encourages exploration and longer session times.
import React, { useMemo, useState, useEffect, useCallback } from 'react';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const InsightsPanel = ({ playerData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [fadeState, setFadeState] = useState('in');

  const insights = useMemo(() => {
    if (!playerData || playerData.length < 3) return [];

    const list = [];
    const divisionData = playerData.filter((i) => i.division);
    const percentileData = playerData.filter((i) => i.percentile);

    // Best weekday
    const weekdayStats = {};
    percentileData.forEach((item) => {
      const day = new Date(item.date).getDay();
      if (!weekdayStats[day]) weekdayStats[day] = { sum: 0, count: 0 };
      weekdayStats[day].sum += item.percentile;
      weekdayStats[day].count += 1;
    });
    const weekdayAvgs = Object.entries(weekdayStats)
      .filter(([, v]) => v.count >= 2)
      .map(([day, v]) => ({ day: Number(day), avg: v.sum / v.count }));
    if (weekdayAvgs.length >= 2) {
      const best = weekdayAvgs.reduce((a, b) => (a.avg < b.avg ? a : b));
      list.push({
        icon: '📅',
        text: <>Your best weekday is <strong>{WEEKDAYS[best.day]}</strong> — you average <strong>top {best.avg.toFixed(1)}%</strong> on that day.</>,
      });
    }

    // Average players beaten
    if (percentileData.length > 0) {
      const avgPercentile = percentileData.reduce((s, i) => s + i.percentile, 0) / percentileData.length;
      list.push({
        icon: '⚔️',
        text: <>On average you beat <strong>{(100 - avgPercentile).toFixed(0)}%</strong> of all COTD players. Not bad!</>,
      });
    }

    // Total players faced
    const totalOpponents = playerData.reduce((s, i) => s + (i.totalPlayers || 0), 0);
    if (totalOpponents > 0) {
      list.push({
        icon: '🌍',
        text: <>You've raced against a total of <strong>{totalOpponents.toLocaleString()}</strong> players across all your COTDs.</>,
      });
    }

    // Best race
    if (percentileData.length > 0) {
      const bestRace = percentileData.reduce((a, b) => (a.percentile < b.percentile ? a : b));
      list.push({
        icon: '🏆',
        text: <>Your best performance was on <strong>{bestRace.map || bestRace.date}</strong> — top <strong>{bestRace.percentile.toFixed(1)}%</strong>!</>,
      });
    }

    // Division consistency
    if (divisionData.length >= 5) {
      const divisions = divisionData.map((i) => i.division);
      const mostCommon = divisions.sort((a, b) =>
        divisions.filter((d) => d === a).length - divisions.filter((d) => d === b).length
      ).pop();
      const share = (divisions.filter((d) => d === mostCommon).length / divisions.length) * 100;
      list.push({
        icon: '🎯',
        text: <>You land in <strong>Division {mostCommon}</strong> most often ({share.toFixed(0)}% of your races).</>,
      });
    }

    // Improvement: first half vs second half
    if (percentileData.length >= 10) {
      const sorted = [...percentileData].sort((a, b) => new Date(a.date) - new Date(b.date));
      const half = Math.floor(sorted.length / 2);
      const firstAvg = sorted.slice(0, half).reduce((s, i) => s + i.percentile, 0) / half;
      const secondAvg = sorted.slice(half).reduce((s, i) => s + i.percentile, 0) / (sorted.length - half);
      const diff = firstAvg - secondAvg;
      if (Math.abs(diff) >= 1) {
        list.push({
          icon: diff > 0 ? '📈' : '📉',
          text: diff > 0
            ? <>You've improved by <strong>{diff.toFixed(1)} percentile points</strong> since your early races. Keep it up!</>
            : <>Your recent races are <strong>{Math.abs(diff).toFixed(1)} points</strong> behind your early form. Time for a comeback?</>,
        });
      }
    }

    // Qualification vs match performance
    const qualiData = playerData.filter((i) => i.qualificationPercentile && i.percentile);
    if (qualiData.length >= 5) {
      const avgQuali = qualiData.reduce((s, i) => s + i.qualificationPercentile, 0) / qualiData.length;
      const avgMatch = qualiData.reduce((s, i) => s + i.percentile, 0) / qualiData.length;
      const diff = avgQuali - avgMatch;
      if (Math.abs(diff) >= 1) {
        list.push({
          icon: diff > 0 ? '🥊' : '⏱️',
          text: diff > 0
            ? <>You're a <strong>match player</strong>: you perform {diff.toFixed(1)} points better in KO rounds than in qualifying.</>
            : <>You're a <strong>quali specialist</strong>: your qualifying is {Math.abs(diff).toFixed(1)} points stronger than your KO rounds.</>,
        });
      }
    }

    // Close calls (rank 9 = first eliminated in KO of 8 advance... simple heuristic: divisionRank 4/5)
    const clutch = playerData.filter((i) => i.divisionRank && i.divisionRank <= 2).length;
    if (clutch >= 2) {
      list.push({
        icon: '💪',
        text: <>You finished <strong>Top 2 in your division {clutch} times</strong> — clutch performer!</>,
      });
    }

    return list;
  }, [playerData]);

  const nextInsight = useCallback(() => {
    setFadeState('out');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % insights.length);
      setFadeState('in');
    }, 250);
  }, [insights.length]);

  // Auto-rotate every 8 seconds
  useEffect(() => {
    if (insights.length <= 1 || isPaused) return;
    const interval = setInterval(nextInsight, 8000);
    return () => clearInterval(interval);
  }, [insights.length, isPaused, nextInsight]);

  if (insights.length === 0) return null;

  const insight = insights[currentIndex % insights.length];

  return (
    <div
      className="mb-6 animate-fade-in-up"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        className="rounded-2xl px-5 py-4 flex items-center gap-4 cursor-pointer transition-all hover:shadow-lg"
        style={{
          background: 'var(--color-glass)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--color-glassBorder)',
        }}
        onClick={nextInsight}
        title="Click for next insight"
      >
        <span className="text-2xl flex-shrink-0">{insight.icon}</span>
        <div
          className="flex-1 text-sm transition-opacity duration-250"
          style={{
            color: 'var(--color-textSecondary)',
            opacity: fadeState === 'in' ? 1 : 0,
          }}
        >
          <span className="text-[10px] uppercase tracking-wider font-bold block mb-0.5" style={{ color: 'var(--color-primary)' }}>
            💡 Insight {currentIndex + 1}/{insights.length}
          </span>
          {insight.text}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {insights.map((_, i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{
                backgroundColor: i === currentIndex ? 'var(--color-primary)' : 'var(--color-border)',
                transform: i === currentIndex ? 'scale(1.3)' : 'scale(1)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default InsightsPanel;
